import { useQuery, UseQueryOptions, UseQueryResult } from 'react-query';
import qs from 'qs';
import { AxiosResponse } from 'axios';
import { SelectItem, User } from '~/types';
import { isActive } from '~/utils/isActive';
import { PAGE_SIZE } from '~/constants';
import api from '../utils/api';
import { getBranches, getHeadOffices } from '~/services/Filter';

export type Group = {
  id_grupoeconomico: number;
  nm_grupoeconomico: string;
  tp_situacao: string;
  dh_ultimaatualizacao: string;
  isActive: string;
  status: boolean;
  segmentos_venda: string;
  responsaveis: string;
  aviso_previo_em_meses: number;
  multa: number;
  dia_limite_solicitar_rescisao: number;
};

export interface GroupView extends Group {
  customer_success: User;
  segmentos_venda_responsavel: SegmentoVendaResponsavel[];
}

export interface GroupViewAsSelect {
  customer_success: User;
  segmentos_venda_responsavel: SegmentosVendaResponsavelAsView[];
}

export interface SegmentosVendaResponsavelAsView {
  segmento: SelectItem;
  responsavel?: SelectItem;
}

export interface SegmentoVendaResponsavel {
  segmento: SegmentoObj;
  responsavel: ResponsaveObj;
}

export type SegmentoObj = {
  id: number;
  nome: string;
};

export type ResponsaveObj = {
  id: number;
  name: string;
};

type GetGroupsResponse = {
  count: number;
  next: boolean | null;
  previous: boolean | null;
  results: Group[];
};

export async function getGroups(
  page: number,
  search = '',
  pageSize: number = PAGE_SIZE,
): Promise<GetGroupsResponse> {
  const { data } = await api.get<GetGroupsResponse>('/grupoeconomico', {
    params: {
      page,
      size: pageSize,
      ...(search ? { search } : {}),
    },
  });

  const groups = data.results.map(group => ({
    ...group,
    status: Boolean(Number(group.tp_situacao)),
    isActive: isActive(Boolean(Number(group.tp_situacao))),
  }));

  return {
    ...data,
    results: groups,
  };
}

export async function getGroupById(id: string): Promise<Group> {
  const { data } = await api.get<Group>(`/grupoeconomico/${id}`);

  return {
    ...data,
    status: Boolean(Number(data.tp_situacao)),
    isActive: isActive(Boolean(Number(data.tp_situacao))),
  };
}

export async function getSegmentoResppnsavelById(
  id: string,
): Promise<GroupViewAsSelect> {
  const { data } = await api.get<GroupView>(`/grupoeconomico/${id}`);

  const segmento = data.segmentos_venda_responsavel.map(it => {
    return {
      segmento: { value: it.segmento.id.toString(), label: it.segmento.nome },
      responsavel: {
        value: it.responsavel?.id.toString(),
        label: it.responsavel?.name,
      },
    };
  });

  return {
    customer_success: data.customer_success,
    segmentos_venda_responsavel: segmento,
  };
}

export async function getGroupsAsSelect(
  pageSize: number = PAGE_SIZE,
  query?: any,
): Promise<SelectItem[]> {
  const { data } = await api.get<GetGroupsResponse>('/grupoeconomico', {
    params: {
      size: pageSize,
      ...{ ...query },
    },
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
  });

  const groups = data.results
    .filter(group => Boolean(Number(group.tp_situacao)) === true)
    .map(group => ({
      value: String(group.id_grupoeconomico),
      label: group.nm_grupoeconomico,
    }));

  return groups;
}

export function useCompanyGroupsAsSelect(
  options: UseQueryOptions<SelectItem[], unknown> = {},
  pageSize: number = PAGE_SIZE,
  query?: SelectItem[],
): UseQueryResult<SelectItem[], unknown> {
  const arrGroups = query?.map(e => e.value);
  return useQuery<SelectItem[], unknown>(
    ['groups', arrGroups],
    () => getGroupsAsSelect(pageSize, { groups: arrGroups }),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export function useMatrizAsSelect(
  options: UseQueryOptions<SelectItem[], unknown> = {},
  queryMatriz?: SelectItem[],
  queryGroup?: SelectItem[],
): UseQueryResult<SelectItem[], unknown> {
  const arrGroup = queryGroup?.map(e => e.value);
  const arrMatriz = queryMatriz?.map(e => e.value);
  return useQuery<SelectItem[], unknown>(
    ['matriz', arrGroup, arrMatriz],
    () => getHeadOffices({ groups: arrGroup, headOffices: arrMatriz }),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export function useFilialAsSelect(
  options: UseQueryOptions<SelectItem[], unknown> = {},
  queryBranch?: SelectItem[],
  queryGroup?: SelectItem[],
  queryMatriz?: SelectItem[],
): UseQueryResult<SelectItem[], unknown> {
  const arrFilial = queryBranch?.map(e => e.value);
  const arrGroups = queryGroup?.map(e => e.value);
  const arrMatriz = queryMatriz?.map(e => e.value);
  return useQuery<SelectItem[], unknown>(
    ['filial', arrFilial, arrMatriz, arrGroups],
    () =>
      getBranches({
        groups: arrGroups,
        branches: arrFilial,
        headOffices: arrMatriz,
      }),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export function useGroups(
  page: number,
  search = '',
  options: UseQueryOptions<GetGroupsResponse, unknown> = {},
  page_size = PAGE_SIZE,
): UseQueryResult<GetGroupsResponse, unknown> {
  return useQuery<GetGroupsResponse, unknown>(
    ['groups', page, search, page_size],
    () => getGroups(page, search, page_size),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export async function getGroupsXlsx(
  params: Record<string, string | number | boolean>,
): Promise<AxiosResponse<Blob>> {
  const url = '/grupoeconomico/xlsx';
  return api.get(url, {
    params,
    headers: {
      'Content-Type': 'application/xlsx',
    },
    responseType: 'blob',
  });
}
